// MobileLayout.tsx
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  Box,
  Paper,
  type SxProps,
  Tab,
  Tabs,
  type Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Eye, Pencil } from 'lucide-react';

import BaseLayout from '@modules/_shared/components/report-editor/layout/BaseLayout';
import MobilePreview from '@modules/_shared/components/report-editor/preview/MobilePreview';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useLocale } from '@shared/hooks/locale';

const MobileLayout = () => {
  const { t } = useLocale();
  const editorEl = useRef<HTMLDivElement>(null);
  const viewerEl = useRef<HTMLDivElement>(null);
  const containerEl = useRef<HTMLDivElement>(null);
  const { mobileTab, setMobileTab } = useReportContext();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const mobileTabRef = useRef(mobileTab);
  mobileTabRef.current = mobileTab;

  // Timer-based suppression: scroll listener is blocked during programmatic
  // scrolls (tab click) to prevent the listener from reverting state mid-animation.
  // Timer is more reliable than scrollend (which can fire from stale scrolls).
  const scrollSuppressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const scrollToTab = useCallback(
    (tab: 'editor' | 'viewer', immediate: boolean) => {
      const container = containerEl.current;
      if (!container) {
        return;
      }
      if (immediate) {
        clearTimeout(scrollSuppressTimer.current);
        scrollSuppressTimer.current = setTimeout(() => {
          scrollSuppressTimer.current = undefined;
        }, 400);
        setMobileTab(tab);
      }
      const targetLeft =
        tab === 'editor' ? 0 : container.scrollWidth - container.clientWidth;
      container.scrollTo({ left: targetLeft, behavior: 'smooth' });
      // Smooth scroll can stop a few px short — force exact position after.
      container.addEventListener(
        'scrollend',
        () => {
          container.scrollLeft = targetLeft;
        },
        { once: true },
      );
    },
    [setMobileTab],
  );

  // Swipe detection: just scrollTo, let scroll listener update tab state.
  useEffect(() => {
    const container = containerEl.current;
    if (!container) {
      return;
    }

    const SWIPE_THRESHOLD = 50;
    let startX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const dx = startX - e.changedTouches[0].clientX;
      if (Math.abs(dx) < SWIPE_THRESHOLD) {
        return;
      }
      const newTab = dx > 0 ? 'viewer' : 'editor';
      if (newTab !== mobileTabRef.current) {
        scrollToTab(newTab, false);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, {
      capture: true,
      passive: true,
    });
    container.addEventListener('touchend', handleTouchEnd, {
      capture: true,
      passive: true,
    });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart, {
        capture: true,
      });
      container.removeEventListener('touchend', handleTouchEnd, {
        capture: true,
      });
    };
  }, [scrollToTab]);

  // Scroll listener: updates tab state for swipe.
  // Suppressed during programmatic scrolls (tab click) via timer.
  useEffect(() => {
    const container = containerEl.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      if (scrollSuppressTimer.current) {
        return;
      }
      const halfway = container.scrollWidth / 2;
      const newTab = container.scrollLeft >= halfway ? 'viewer' : 'editor';
      if (newTab !== mobileTabRef.current) {
        setMobileTab(newTab);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [setMobileTab]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, value: 'viewer' | 'editor') => {
      scrollToTab(value, true);
    },
    [scrollToTab],
  );

  const tabStyles: SxProps<Theme> = useMemo(
    () => ({
      '.MuiButtonBase-root': {
        minWidth: '0',
      },
      '.MuiTabs-indicator': {
        backgroundColor: (theme.vars ?? theme).palette.primary.main,
        borderRadius: '0 0 3px 3px',
        top: 0,
        transition: 'all 50ms ease-in-out',
      },
      '.Mui-selected': {
        color: (theme.vars ?? theme).palette.text.primary,
        fontWeight: '500',
      },
    }),
    [theme],
  );

  return (
    <>
      <BaseLayout
        editorEl={editorEl}
        containerEl={containerEl}
        mode="mobile"
        renderPreview={() => (
          <Box
            ref={viewerEl}
            css={{
              minWidth: '100vw',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <MobilePreview />
          </Box>
        )}
      />

      {isTablet && (
        <Paper
          square
          elevation={4}
          sx={{
            width: '100%',
            borderTop: `1px solid ${(theme.vars ?? theme).palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tabs sx={tabStyles} value={mobileTab} onChange={handleTabChange}>
              <Tab
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Pencil size={18} style={{ marginRight: 8 }} />
                    {t('editor')}
                  </Box>
                }
                value="editor"
              />
              <Tab
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Eye size={18} style={{ marginRight: 8 }} />
                    {t('preview')}
                  </Box>
                }
                value="viewer"
              />
            </Tabs>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default MobileLayout;
